"use client";
import React from "react";
import ContactUsButton from "../ui/contactButton";
import { Logo } from "@/assets/icons";
import Link from "next/link";
import Image from "next/image";
import CyberIreland from "../../../public/Cyber Ireland Badge 26-27.png";

/**
 * Footer component
 *
 * @returns {React.JSX.Element} The rendered footer component.
 */
const Footer: React.FC = () => {
  return (
    <footer
      className="w-full max-w-[1600px] mx-auto"
      style={{ boxShadow: "0 -0.9px 0 rgba(0, 0, 0, 0.1)" }}
    >
      <div className="mx-auto px-6 sm:px-12 lg:px-16 py-12 sm:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
        {/* Logo and Description */}
        <div className="col-span-1 flex flex-col items-start">
          <div className="flex items-center gap-3 mb-4">
            <Logo className="w-12 h-12 sm:w-14 sm:h-14" />
            <span className="text-2xl sm:text-[30px] tracking-tight font-bold text-[#072138]">
              Trust Vector
            </span>
          </div>
          <p className="text-secondary-gray font-medium text-base sm:text-lg mb-6 max-w-sm">
            Precision Security for Modern Enterprises.
          </p>
          <Link href={"https://cyberireland.ie/"} target="_blank">
            <Image src={CyberIreland} alt="Cyber Ireland" width={140} height={140} className="object-contain" />
          </Link>
        </div>

        {/* Company Links */}
        <div className="col-span-1">
          <h3 className="font-semibold text-typography-color text-base mb-6">
            Company
          </h3>
          <ul className="space-y-4 text-sm font-medium">
            <li>
              <Link
                href="/product-services"
                className="text-typography-color hover:text-primary transition-colors"
              >
                AI Products
              </Link>
            </li>
            <li>
              <Link
                href="/membaly"
                className="text-typography-color hover:text-primary transition-colors"
              >
                Membaly (Club Management)
              </Link>
            </li>
            <li>
              <Link
                href="/cybersecurity-compliances"
                className="text-typography-color hover:text-primary transition-colors"
              >
                Cybersecurity & Compliances
              </Link>
            </li>
            <li>
              <Link
                href="/case-studies"
                className="text-typography-color hover:text-primary transition-colors"
              >
                Case Studies
              </Link>
            </li>
            <li>
              <Link
                href="/timeless-marketing"
                className="text-typography-color hover:text-primary transition-colors"
              >
                Timeless Marketing (Case Study)
              </Link>
            </li>
            <li>
              <Link
                href="/about-us"
                className="text-typography-color hover:text-primary transition-colors"
              >
                About Us
              </Link>
            </li>
          </ul>
        </div>

        {/* Legal Links */}
        <div className="col-span-1">
          <h3 className="font-semibold text-typography-color text-base mb-6">
            Legal
          </h3>
          <ul className="space-y-4 text-sm font-medium">
            <li>
              <Link
                href="/terms-of-use"
                className="text-typography-color hover:text-primary transition-colors"
              >
                Terms of Use
              </Link>
            </li>
            <li>
              <Link
                href="/privacy-policy"
                className="text-typography-color hover:text-primary transition-colors"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                href="/cookies-policy"
                className="text-typography-color hover:text-primary transition-colors"
              >
                Cookies Policy
              </Link>
            </li>
          </ul>
        </div>

        {/* CTA Section */}
        <div className="col-span-1 flex flex-col items-start mb-20 sm:mb-0">
          <h2 className="text-lg sm:text-xl font-semibold text-typography-color leading-snug mb-3">
            Ready to Revolutionize Your AI-Powered{" "}
            <span className="text-primary">Cybersecurity Management?</span>
          </h2>
          <div className="flex flex-wrap items-center gap-3.5 mt-4">
            <ContactUsButton />
            <button
              type="button"
              onClick={() => window.open("https://applycyber.net/", "_blank")}
              className="inline-flex items-center justify-center bg-transparent text-primary border border-primary hover:bg-primary/90 cursor-pointer hover:text-white hover:border-secondary-gray px-6 py-2.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-all duration-300"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>
      <div className="w-[75%] sm:block hidden mx-auto border-4 border-primary" />
    </footer>
  );
};

export default Footer;
